import mixpanel from 'mixpanel-browser';
import { MyOrg, UserInfo } from '@/lib/models';
import { isBillingAdmin, isBillingViewer, isOrgAdmin } from '@/pages/admin/user/type';
import { getUserFullname } from '@/utils/utils';
import { toUtcIso } from '@/utils/analytics/time';
import type { OrganizationRole } from '@/utils/analytics/taxonomy';
import type { OnboardingPath } from '@/utils/analytics/types';

// The browser writes only the user-profile properties cloud-universe cannot see.
//
// Everything else — user_email, created_at, signup_method, email_domain_type,
// email_verified*, organization_id, is_internal_user, and every Organization, Workgroup
// and Workspace group profile — is owned by cloud-universe, which writes it from
// canonical backend state at the moment that state actually changes. Two writers on one
// property means whichever ran last wins, so nothing below may overlap with that set.
//
// See utils/analytics/events/Mixpanel Events - Entity State Update Matrix.csv, Rule 3.

export function deriveOrganizationRole(userInfo: UserInfo): OrganizationRole {
  if (isOrgAdmin(userInfo.roles)) {
    return 'organization_admin';
  }
  if (isBillingViewer(userInfo.roles) && !isBillingAdmin(userInfo.roles)) {
    const hasNonBillingRole = userInfo.roles.some(
      (role) => !['billing-viewers', 'billing-admins', 'users'].includes(role.name)
    );
    if (!hasNonBillingRole) {
      return 'viewer';
    }
  }
  return 'member';
}

export type SyncUserProfileInput = {
  userInfo?: UserInfo;
  currentOrg?: MyOrg;
};

/**
 * Write the portal-owned slice of the Mixpanel user profile.
 *
 * `last_login_at` comes from /users/me, which reports the *previous* successful login
 * rather than the current one. ACT-011 wants the current authentication timestamp, but
 * sign_in_completed is not implemented in either repo, so this is the closest available
 * source. It lags by one session — do not derive active-user counts from it.
 */
export function updateUserProfile({ userInfo, currentOrg }: SyncUserProfileInput) {
  if (!userInfo?.id) {
    return;
  }

  const props: Record<string, unknown> = {
    user_name: getUserFullname(userInfo),
    organization_role: deriveOrganizationRole(userInfo),
  };

  const lastLoginAt = toUtcIso(userInfo.metadata?.lastLogin);
  if (lastLoginAt) {
    props.last_login_at = lastLoginAt;
  }

  // currentOrg is accepted so the role stays scoped to the organization it was derived
  // from; organization_id itself remains a cloud-universe property.
  void currentOrg;

  mixpanel.people.set(props);
}

/**
 * Record the onboarding path the user committed to on the Get Started form.
 *
 * Portal owns this one because the selection only exists in the browser: the survey
 * submit persists the company details but not the chosen path, so no backend service can
 * observe it. Called after the submit succeeds, never from the card click, so an
 * abandoned form leaves no trace.
 */
export function updateOnboardingPath(onboardingPath: OnboardingPath) {
  mixpanel.people.set({ onboarding_path: onboardingPath });
}
